import React from 'react';
import { Container, Box, Typography, Link } from '@mui/material';
import { useThemeMode } from '../contexts/ThemeModeContext';

const PrivacyPolicy: React.FC = () => {
  const { mode } = useThemeMode();

  return (
    <Box sx={{
      minHeight: '100vh',
      background: mode === 'dark'
        ? 'linear-gradient(180deg, #0f172a 0%, #151b26 25%, #1a2332 50%, #1c2838 75%, #1e293b 100%)'
        : 'linear-gradient(180deg, #FAFBFC 0%, #FAFAFC 25%, #F9FAFB 50%, #F9FAFC 75%, #F8FAFC 100%)',
      pt: { xs: 8, md: 12 },
      pb: { xs: 6, md: 10 },
    }}>
      <Container maxWidth="md">
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 700,
            color: mode === 'dark' ? '#ffffff' : '#0f172a',
            mb: 4,
          }}
        >
          Privacy Policy
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : '#64748b',
            mb: 4,
          }}
        >
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              1. Information We Collect
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              We collect information that you and your organization provide directly to us, as well as information generated through your use of the Services. This includes:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569' }}>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Organization and account information (company name, business contact details, billing and subscription information).</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>User account information (name, work email address, role, authentication identifiers, and access permissions).</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Content you upload or connect, including documentation, policies, knowledge base materials, and other reference content used to power your AI agents.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Support tickets, chat transcripts, comments, and other interactions with our AI agents and human operators.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Technical and usage data, such as IP address, device and browser information, log data, performance metrics, and feature usage patterns.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Configuration data (tenant settings, routing rules, roles, teams, and integrations with third-party systems).</Typography></li>
            </Box>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              2. How We Use Your Information
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              We use the information we collect to operate, secure, and improve the Services. This includes:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569' }}>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Providing, maintaining, and improving our AI-powered support services, including ticketing, chat, knowledge base, and analytics features.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Configuring and operating organization-specific AI agents based on your content and settings.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Processing and responding to support requests, tickets, and conversations initiated by you or your end users.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Monitoring performance, security events, and usage patterns to detect abuse, troubleshoot issues, and enhance reliability.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Communicating with you about your account, service updates, billing, and product changes.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Generating aggregated, de-identified analytics to understand platform usage and improve our products, without identifying individual customers or end users.</Typography></li>
            </Box>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              3. Data Security and Isolation
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
              }}
            >
              We implement enterprise-grade security measures to protect your data. Each organization (tenant) is provisioned with dedicated, logically isolated infrastructure. We use cloud services such as AWS (including but not limited to S3, DynamoDB, Bedrock, SES, and OpenSearch) and follow industry-standard security practices, including:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569', mt: 1 }}>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Network-level isolation between tenants and restricted administrative access.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Encryption at rest and in transit where supported by the underlying cloud services.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Audit logging and monitoring of access to sensitive systems.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Role-based access control to limit internal and external access to customer environments.</Typography></li>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mt: 2,
              }}
            >
              Your data is not shared with other organizations, and we maintain strict access controls to ensure data privacy and confidentiality. Where we use subprocessors (such as cloud providers), we require them to implement appropriate security and confidentiality measures.
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              4. Data Retention and Deletion
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
              }}
            >
              We retain your information for as long as your organization maintains an active subscription or as needed to provide the Services, comply with legal obligations, resolve disputes, and enforce our agreements. Specific retention periods may vary based on the type of data and your configuration.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mt: 2,
              }}
            >
              You may request deletion of certain data at any time by contacting us. Upon account termination, we will delete or anonymize Customer Content in accordance with our standard retention schedules and applicable laws, unless a longer retention period is required or permitted by law or agreed in a separate contract.
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              5. Your Rights and Choices
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              Depending on your jurisdiction and role (for example, if you are an account owner, administrator, or end user), you may have certain rights with respect to your personal data. Subject to applicable law, you may:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569' }}>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Access and review certain personal information we hold about you.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Request correction of inaccurate or incomplete data.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Request deletion of certain personal data, subject to legal and contractual limitations.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Object to or restrict certain types of processing.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Request export of your data in a structured, commonly used format, where technically feasible.</Typography></li>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mt: 2,
              }}
            >
              Many of these rights are exercised through your organization's administrators, who control access to and configuration of the tenant environment. Where we act as a data processor on behalf of your organization, we will direct individual data subject requests to the relevant customer, unless otherwise required by law.
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              6. International Transfers, Subprocessors, and Contact
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              We may process and store your information in jurisdictions other than your own, including in the United States, using reputable cloud providers. Where required, we implement appropriate safeguards for cross-border data transfers, such as standard contractual clauses or other legally recognized mechanisms.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              We may engage third-party subprocessors (for example, cloud hosting providers, email delivery services, logging and monitoring tools) to support the operation of the Services. We require such subprocessors to implement appropriate technical and organizational measures to protect personal data and to process it only as necessary to provide their services to us.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
              }}
            >
              If you have questions about this Privacy Policy, our data protection practices, or would like to exercise your rights, please contact us at:{' '}
              <Link href="mailto:privacy@coresupportai.com" sx={{ color: '#6366f1' }}>
                privacy@coresupportai.com
              </Link>
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;

